<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <ExactSearch
    @change-exact-search-settings="
      $emit('change-exact-search-settings', $event)
    "
    :disabled="hasActiveCriteria || isSavedViewLoaded"
  />
  <PrimeAccordion class="mt-4" multiple :value="['0']">
    <PrimeAccordionPanel value="0">
      <PrimeAccordionHeader>{{
        $t("vuesFavorites.title")
      }}</PrimeAccordionHeader>
      <PrimeAccordionContent>
        <UserViewsFavoritesPanel
          ref="savedSearchesPanel"
          @load-view="handleLoadView"
        />
      </PrimeAccordionContent>
    </PrimeAccordionPanel>
    <PrimeAccordionPanel value="1">
      <PrimeAccordionHeader>{{
        $t("vuesUtilisateurs.queryBuilder")
      }}</PrimeAccordionHeader>
      <PrimeAccordionContent>
        <div
          v-if="isSavedViewLoaded"
          class="p-2 mb-3 border-1 border-round border-blue-200 bg-blue-50 text-blue-700"
        >
          {{ $t("vuesUtilisateurs.loadedViewMessage") }}
          <span class="font-bold">{{ loadedView.name }}</span>
        </div>
        <div class="p-fluid formgrid grid">
          <div class="field col-12">
            <label for="locationType">{{
              $t("vuesUtilisateurs.locationLabel")
            }}</label>
            <PrimeSelectButton
              id="locationType"
              v-model="locationModel.type"
              :options="locationOptions"
              optionLabel="label"
              optionValue="value"
              allowEmpty
              class="w-full"
            />
          </div>
          <div class="field col-12" v-if="locationModel.type">
            <label for="locationValue">{{
              $t("vuesUtilisateurs.valueLabel")
            }}</label>
            <PrimeInputText
              id="locationValue"
              :value="locationModel.value"
              :placeholder="locationPlaceholder"
              @click="
                locationModel.type === 'Folder'
                  ? (isDossierTreeVisible = true)
                  : null
              "
              @change="updateLocationValue($event.target.value)"
            />
            <small v-if="locationModel.type === 'Folder'" id="username-help"
              >Ex: workspace://SpacesStore/{ID du dossier}</small
            >
          </div>
        </div>

        <div class="mt-4">
          <h5>{{ $t("vuesUtilisateurs.criteriaTitle") }}</h5>
          <div
            v-if="criteria.length === 0"
            class="text-center text-gray-500 p-3 border-1 border-dashed border-round"
          >
            {{ $t("vuesUtilisateurs.noCriteria") }}
          </div>
          <div v-else class="flex flex-column gap-2">
            <div
              v-for="(criterion, index) in criteria"
              :key="criterion.id"
              class="flex align-items-center p-2 border-1 border-round border-gray-200"
            >
              <div class="flex-grow-1 flex flex-wrap gap-1">
                <span class="font-bold">{{
                  $t(criterion.property.labelId)
                }}</span>
                <span
                  :class="criterion.negation ? 'text-red-500' : 'text-primary'"
                  >{{
                    criterion.negation
                      ? $t("vuesUtilisateurs.mustNotContain")
                      : $t("vuesUtilisateurs.mustContain")
                  }}</span
                >
                <span class="font-italic">{{
                  $t(criterion.operatorLabelKey)
                }}</span>
                <span class="text-gray-700">{{
                  formatCriterionValueForDisplay(criterion)
                }}</span>
              </div>
              <PrimeButton
                icon="ri-delete-bin-line"
                :aria-label="$t('delete')"
                text
                rounded
                severity="danger"
                @click="removeCriterion(index)"
              />
            </div>
          </div>
          <PrimeButton
            icon="ri-add-line"
            :label="$t('vuesUtilisateurs.addCriterionButton')"
            @click="isAddCriterionDialogVisible = true"
            class="mt-3 w-full"
            outlined
          />
          <PrimeButton
            v-if="hasActiveCriteria"
            icon="ri-save-3-line"
            :label="$t('vuesUtilisateurs.saveView')"
            @click="openSaveViewPopup"
            class="mt-3 w-full"
          />
          <div v-if="canManageLoadedView" class="flex gap-2 mt-2">
            <PrimeButton
              icon="ri-pencil-line"
              :label="$t('vuesUtilisateurs.modifyView')"
              @click="modifyView"
              class="w-full"
              severity="secondary"
            />
            <PrimeButton
              icon="ri-delete-bin-line"
              :label="$t('vuesUtilisateurs.deleteView')"
              @click="deleteView"
              class="w-full"
              severity="danger"
            />
          </div>
        </div>
      </PrimeAccordionContent>
    </PrimeAccordionPanel>
  </PrimeAccordion>

  <AddCriterionPopup
    v-model:visible="isAddCriterionDialogVisible"
    :properties="availableProperties"
    @add-criterion="handleCriterionAdded"
  />

  <SaveViewPopup
    v-model:visible="saveViewDialogVisible"
    :view-data="viewToEdit"
    :error-message="saveViewError"
    @save-view="handleSaveView"
    @clear-error="clearSaveViewError"
  />

  <PrimeDialog
    v-model:visible="isDossierTreeVisible"
    header="Choisir un dossier"
    modal
    class="w-full md:w-6"
  >
    <TreeExplorer
      :current-node="null"
      showUserHome
      @select-node="handleDossierSelected"
    />
  </PrimeDialog>

  <PrimeDialog
    v-model:visible="isDeleteConfirmVisible"
    header="Confirmation de suppression"
    modal
    class="dialog-confirm-delete"
  >
    <div class="flex align-items-center">
      <i class="ri-alert-line mr-3 text-4xl" aria-hidden="true" />
      <span v-if="loadedView">{{
        $t("vuesUtilisateurs.confirmDelete", { name: loadedView.name })
      }}</span>
    </div>
    <template #footer>
      <PrimeButton
        :label="$t('no')"
        icon="ri-close-line"
        @click="isDeleteConfirmVisible = false"
        class="p-button-text"
      />
      <PrimeButton
        :label="$t('yes')"
        icon="ri-check-line"
        @click="handleDeleteConfirmation"
        class="p-button-danger"
        autofocus
      />
    </template>
  </PrimeDialog>
</template>
<script>
import { mapState, mapActions } from "pinia";
import { useUserViewStore } from "@/stores/userView.js";
import { useConfigStore } from "@/stores/config";
import { useUserStore } from "@/stores/user";
import TreeExplorer from "@/components/TreeExplorer.vue";

import ExactSearch from "./ExactSearch.vue";
import Accordion from "primevue/accordion";
import AccordionPanel from "primevue/accordionpanel";
import AccordionHeader from "primevue/accordionheader";
import AccordionContent from "primevue/accordioncontent";
import UserViewsFavoritesPanel from "./UserViewsFavoritesPanel.vue";
import SelectButton from "primevue/selectbutton";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import AddCriterionPopup from "@/components/search/AddCriterionPopup.vue";
import SaveViewPopup from "@/components/search/SaveViewPopup.vue";
import Dialog from "primevue/dialog";

export default {
  name: "AdvancedSearchPanel",
  components: {
    ExactSearch,
    PrimeAccordion: Accordion,
    PrimeAccordionPanel: AccordionPanel,
    PrimeAccordionHeader: AccordionHeader,
    PrimeAccordionContent: AccordionContent,
    UserViewsFavoritesPanel,
    PrimeSelectButton: SelectButton,
    PrimeInputText: InputText,
    PrimeButton: Button,
    AddCriterionPopup,
    SaveViewPopup,
    PrimeDialog: Dialog,
    TreeExplorer,
  },
  emits: ["change-exact-search-settings", "load-view"],
  watch: {
    "location.type"(newValue) {
      if (!newValue) {
        this.updateLocationValue("");
      }
    },
  },
  data() {
    return {
      isAddCriterionDialogVisible: false,
      saveViewDialogVisible: false,
      saveViewDialogMode: "create",
      saveViewError: null,
      isDossierTreeVisible: false,
      isDeleteConfirmVisible: false,
    };
  },
  mounted() {
    this.checkPristyAdminStatus();
  },
  computed: {
    ...mapState(useUserStore, ["isPristyAdmin"]),
    ...mapState(useUserViewStore, ["location", "criteria", "loadedView"]),
    locationModel: {
      get() {
        return this.location;
      },
      set(value) {
        this.setLocation(value);
      },
    },
    locationOptions() {
      return [
        { label: this.$t("vuesUtilisateurs.locationSite"), value: "Site" },
        { label: this.$t("folder"), value: "Folder" },
      ];
    },
    locationPlaceholder() {
      if (this.locationModel.type === "Site") {
        return this.$t("vuesUtilisateurs.sitePlaceholder");
      }
      if (this.locationModel.type === "Folder") {
        return this.$t("vuesUtilisateurs.folderPlaceholder");
      }
      return "";
    },
    hasActiveCriteria() {
      const hasLocation =
        this.location && this.location.type && this.location.value;
      return !!((this.criteria && this.criteria.length > 0) || hasLocation);
    },
    isSavedViewLoaded() {
      return this.loadedView !== null;
    },
    canManageLoadedView() {
      if (!this.isSavedViewLoaded) {
        return false;
      }
      if (this.loadedView.scope === "user") {
        return true;
      }
      if (this.loadedView.scope === "global") {
        return this.isPristyAdmin;
      }
      return false;
    },
    viewToEdit() {
      return this.saveViewDialogMode === "edit" ? this.loadedView : null;
    },
    availableProperties() {
      const configStore = useConfigStore();
      return configStore.METADATA.filter(
        (meta) =>
          meta.display &&
          (meta.display.includes("view") ||
            meta.display.includes("advancedSearch")),
      );
    },
  },
  methods: {
    ...mapActions(useUserStore, ["checkPristyAdminStatus"]),
    ...mapActions(useUserViewStore, [
      "setLocation",
      "addCriterion",
      "removeCriterion",
      "saveCurrentView",
      "updateCurrentView",
      "deleteCurrentView",
    ]),
    clearSaveViewError() {
      this.saveViewError = null;
    },
    handleDossierSelected(node) {
      this.updateLocationValue(node.id);
      this.isDossierTreeVisible = false;
    },
    openSaveViewPopup() {
      this.saveViewDialogMode = "create";
      this.saveViewDialogVisible = true;
    },
    modifyView() {
      this.saveViewDialogMode = "edit";
      this.saveViewDialogVisible = true;
    },
    deleteView() {
      this.isDeleteConfirmVisible = true;
    },
    async handleDeleteConfirmation() {
      this.isDeleteConfirmVisible = false;
      try {
        await this.deleteCurrentView();
        this.$toast.add({
          severity: "success",
          summary: "Succès",
          detail: "Recherche supprimée avec succès.",
          life: 3000,
        });
        this.$refs.savedSearchesPanel.fetchSavedViews();
      } catch (error) {
        this.$toast.add({
          severity: "error",
          summary: "Erreur",
          detail: error.message,
          life: 5000,
        });
      }
    },
    async handleSaveView(payload) {
      this.clearSaveViewError();

      const isCreateMode = this.saveViewDialogMode === "create";
      let action;
      let searchData;
      let successMessageKey;

      const config = {
        location: JSON.parse(JSON.stringify(this.location)),
        criteria: JSON.parse(JSON.stringify(this.criteria)),
      };

      if (isCreateMode) {
        action = this.saveCurrentView;
        successMessageKey = "vuesUtilisateurs.addsSavedViewSuccess";
        searchData = {
          id: crypto.randomUUID(),
          name: payload.name,
          description: payload.description,
          scope: payload.scope,
          config,
        };
      } else {
        action = this.updateCurrentView;
        successMessageKey = "vuesUtilisateurs.modifySavedViewSuccess";
        searchData = {
          ...this.loadedView,
          name: payload.name,
          description: payload.description,
          config,
        };
      }

      try {
        await action(searchData);
        this.$toast.add({
          severity: "success",
          summary: "Succès",
          detail: this.$t(successMessageKey),
          life: 3000,
        });
        this.$refs.savedSearchesPanel.fetchSavedViews();
        this.saveViewDialogVisible = false;
      } catch (error) {
        this.$toast.add({
          severity: "error",
          summary: "Erreur",
          detail: error.message,
          life: 5000,
        });
      }
    },
    handleLoadView(searchConfig) {
      this.$emit("load-view", searchConfig);
    },
    updateLocationValue(newValue) {
      let finalValue = newValue;
      if (
        this.locationModel.type === "Folder" &&
        newValue &&
        !newValue.startsWith("workspace://")
      ) {
        finalValue = `workspace://SpacesStore/${newValue}`;
      }
      this.setLocation({ ...this.locationModel, value: finalValue });
    },
    handleCriterionAdded(criterion) {
      this.addCriterion(criterion);
    },
    formatCriterionValueForDisplay(criterion) {
      if (criterion.value === null || criterion.value === undefined) {
        return "";
      }
      if (criterion.operator === "range") {
        const start =
          criterion.value instanceof Date
            ? new Date(criterion.value).toLocaleDateString()
            : criterion.value || "*";
        const end =
          criterion.value_end instanceof Date
            ? new Date(criterion.value_end).toLocaleDateString()
            : criterion.value_end || "*";
        return `[${start} -> ${end}]`;
      } else if (criterion.value instanceof Date) {
        return new Date(criterion.value).toLocaleDateString();
      } else if (typeof criterion.value === "boolean") {
        return this.$t(criterion.value ? "true" : "false");
      } else {
        return `"${criterion.value}"`;
      }
    },
  },
};
</script>
<!-- Non-scoped: Dialog teleported to body -->
<style>
.dialog-confirm-delete {
  width: 350px;
}
</style>
